import {
    Box,
    Container,
    Grid,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
} from "../../../../components/ui/LayoutPrimitives";
import React from "react";
import { KRUMAGO } from "../../../s3-assets/Krumago";
import { useSelector } from "react-redux";
import SocialIcons from "../../../../assets/SocialIcons";
import { useNavigate } from "react-router-dom";
import { formatPhoneNumber } from "../../../../config/locationConfig";

const AvikarFooter = () => {
    const companyDetails = useSelector((state) => state.companyReducer.company);
    const navigate = useNavigate();

    // Format camelCase / snake_case / PascalCase text
    const formatText = (text) => {
        if (!text) return "";

        return text
            .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase -> camel Case
            .replace(/_/g, " ") // snake_case -> snake case
            .replace(/\s+/g, " ")
            .trim()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const companyName = formatText(companyDetails?.name || "Company Name");

    // Function to get display phone number with country code
    const getDisplayPhoneNumber = () => {
        const rawPhoneNumber =
            companyDetails?.contactPerson?.phoneNumber ||
            companyDetails?.contactPerson?.mobile ||
            companyDetails?.phoneNumber ||
            "";

        const rawCountryCode =
            companyDetails?.contactPerson?.countryCode ||
            companyDetails?.countryCode ||
            "";

        return rawPhoneNumber
            ? formatPhoneNumber(rawPhoneNumber, rawCountryCode || companyDetails)
            : "Contact Number";
    };

    const email =
        companyDetails?.contactPerson?.email?.toLowerCase() ||
        "support@example.com";

    // Get legal documents from company
    const legalDocuments = companyDetails?.legalDocuments;

    // Format contentSubType for display
    const formatContentSubType = (contentSubType) => {
        if (!contentSubType) return "Legal Document";

        return contentSubType
            .toLowerCase()
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    // Handle policy link click
    const handlePolicyClick = (doc, event) => {
        event.preventDefault();

        if (doc.contentSubType) {
            const policyName = doc.contentSubType.toLowerCase();
            navigate(`/policy/${policyName}`);
        }
    };

    // Transform legal documents for quick links
    const quickLinks =
        legalDocuments?.length > 0
            ? legalDocuments.map((doc) => ({
                text:
                    formatContentSubType(doc.contentSubType) ||
                    doc.name ||
                    "Legal Document",

                document: doc,
                link: "#",
            }))
            : null;

    return (
        <Box
            component="footer"
            sx={{
                bgcolor: "#fdfaf6",
                color: "#000",
                py: 8,
                mt: 8,
            }}
        >
            <Container maxWidth="xl">
                <Grid container spacing={4} justifyContent="space-between">
                    {/* Company Name and Social Icons */}
                    <Grid item xs={12} sm={3} md={3}>
                        <Box textAlign="left">
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: "bold",
                                    textTransform: "none",
                                    fontFamily: "Garamond",
                                }}
                            >
                                {companyName}
                            </Typography>

                            <SocialIcons />
                        </Box>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={12} sm={3} md={3}>
                        <Box textAlign="left">
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                QUICK LINK
                            </Typography>

                            {quickLinks ? (
                                <List sx={{ padding: 0 }}>
                                    {quickLinks.map((item) => (
                                        <ListItem
                                            key={item.text}
                                            disableGutters
                                            sx={{ padding: "2px 0" }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Link
                                                        href={item.link}
                                                        underline="hover"
                                                        onClick={(e) =>
                                                            item.document
                                                                ? handlePolicyClick(
                                                                    item.document,
                                                                    e
                                                                )
                                                                : null
                                                        }
                                                        sx={{
                                                            fontSize: "0.875rem",
                                                            color: "#000",
                                                            fontFamily:
                                                                "'Arial', sans-serif",
                                                            "&:hover": {
                                                                color: "#757575",
                                                                cursor: "pointer",
                                                            },
                                                        }}
                                                    >
                                                        {item.text}
                                                    </Link>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: "0.875rem",
                                        color: "text.secondary",
                                        fontStyle: "italic",
                                        mt: 1,
                                    }}
                                >
                                    Legal documents are currently unavailable
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Customer Service */}
                    <Grid item xs={12} sm={3} md={3}>
                        <Box textAlign="start">
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                CUSTOMER SERVICE
                            </Typography>

                            <List sx={{ padding: 0 }}>
                                {[
                                    {
                                        text: "Contact Us",
                                        link: "/contact",
                                        id: "contact-us",
                                    },
                                ].map((item) => {
                                    const id =
                                        item.id ||
                                        `customer-policy-${item.text
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")}`;

                                    return (
                                        <ListItem
                                            key={item.text}
                                            disableGutters
                                            sx={{ padding: "2px 0" }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Link
                                                        id={id}
                                                        href={item.link}
                                                        underline="hover"
                                                        sx={{
                                                            fontSize: "0.875rem",
                                                            color: "#000",
                                                            fontFamily:
                                                                "'Arial', sans-serif",
                                                            "&:hover": {
                                                                color: "#757575",
                                                            },
                                                        }}
                                                    >
                                                        {item.text}
                                                    </Link>
                                                }
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>
                    </Grid>

                    {/* Get in Touch */}
                    <Grid item xs={12} sm={3} md={3}>
                        <Box textAlign="start">
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                GET IN TOUCH
                            </Typography>

                            <Typography variant="body2">
                                📞 {getDisplayPhoneNumber()}
                            </Typography>

                            <Typography variant="body2">
                                ✉️ {email}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <hr className="my-4" />

            {/* Footer Bottom */}
            <Typography
                align="center"
                sx={{
                    color: "#666",
                    fontSize: "0.9rem",
                }}
            >
                © {new Date().getFullYear()} {companyName}. All rights reserved.
            </Typography>

            <Box textAlign="center" mt={1}>
                <Typography
                    component="span"
                    sx={{
                        color: "#666",
                        fontSize: "0.9rem",
                    }}
                >
                    <strong>Powered by</strong>{" "}
                </Typography>

                <img
                    loading="lazy"
                    src={KRUMAGO.IMAGES.KRUMAGO_IMG}
                    alt="Krumago"
                    className="max-h-6 inline-block align-middle ml-1 cursor-pointer"
                />

                <br />

                <a
                    href="https://www.krumago.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                >
                    www.krumago.com
                </a>
            </Box>
        </Box>
    );
};

export default AvikarFooter;
